import { Check, ListChecks, Pencil, Plus, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import {
	useCreateSpheraOption,
	useDeleteSpheraOption,
	useRenameSpheraOption,
	useSpheraOptions,
} from '@/api/queries/sphera-export-queries'
import {
	SpheraBranchList,
	SpheraCrList,
	SpheraSettingsTab,
} from '@/components/administration'
import { AdminCardPage, ConfirmDialog } from '@/components/commons'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { adminBackRoute } from '@/constants/admin-menu'
import { ROUTES } from '@/constants/routes'
import type { SpheraOptionItem, SpheraOptionType } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'

function OptionRow({
	item,
	onDelete,
}: {
	item: SpheraOptionItem
	onDelete: (item: SpheraOptionItem) => void
}) {
	const { t } = useTranslation()
	const [editing, setEditing] = useState(false)
	const [name, setName] = useState(item.name)
	const rename = useRenameSpheraOption()

	const save = async () => {
		const next = name.trim()
		if (!next || next === item.name) {
			setEditing(false)
			return
		}
		try {
			await rename.mutateAsync({ id: item.id, name: next })
			toast.success(t('administration.spheraOptions.renamed'))
			setEditing(false)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	return (
		<li className="flex items-center gap-2 py-2">
			{editing ? (
				<Input
					autoFocus
					value={name}
					onChange={(e) => setName(e.target.value)}
					onKeyDown={(e) => e.key === 'Enter' && save()}
					className="h-8 flex-1"
				/>
			) : (
				<span className="min-w-0 flex-1 truncate text-sm text-kaefer-dark-grey">
					{item.name}
				</span>
			)}
			<span className="shrink-0 text-xs text-muted-foreground">
				{item.usageCount === 0
					? t('administration.spheraOptions.usageNone')
					: t('administration.spheraOptions.usage', { count: item.usageCount })}
			</span>
			{editing ? (
				<>
					<Button
						variant="ghost"
						size="icon-xs"
						disabled={rename.isPending}
						onClick={save}
					>
						<Check className="size-3.5" />
					</Button>
					<Button
						variant="ghost"
						size="icon-xs"
						onClick={() => {
							setName(item.name)
							setEditing(false)
						}}
					>
						<X className="size-3.5" />
					</Button>
				</>
			) : (
				<>
					<Button
						variant="ghost"
						size="icon-xs"
						onClick={() => setEditing(true)}
					>
						<Pencil className="size-3.5" />
					</Button>
					<Button
						variant="ghost"
						size="icon-xs"
						disabled={item.usageCount > 0}
						title={
							item.usageCount > 0
								? t('administration.spheraOptions.inUse')
								: undefined
						}
						onClick={() => onDelete(item)}
						className="text-kaefer-red"
					>
						<Trash2 className="size-3.5" />
					</Button>
				</>
			)}
		</li>
	)
}

function OptionSection({
	type,
	title,
	items,
	onDelete,
}: {
	type: SpheraOptionType
	title: string
	items: SpheraOptionItem[]
	onDelete: (item: SpheraOptionItem) => void
}) {
	const { t } = useTranslation()
	const [name, setName] = useState('')
	const create = useCreateSpheraOption()

	const add = async () => {
		if (!name.trim()) return
		try {
			await create.mutateAsync({ type, name: name.trim() })
			toast.success(t('administration.spheraOptions.created'))
			setName('')
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	return (
		<section className="min-w-0 space-y-2">
			<h3 className="text-sm font-semibold text-kaefer-dark-grey">
				{title} ({items.length})
			</h3>
			<div className="flex items-center gap-2">
				<Input
					className="min-w-0 flex-1"
					value={name}
					onChange={(e) => setName(e.target.value)}
					onKeyDown={(e) => e.key === 'Enter' && add()}
					placeholder={t('administration.spheraOptions.newPlaceholder')}
				/>
				<Button
					onClick={add}
					disabled={!name.trim() || create.isPending}
					className="w-auto shrink-0 gap-1.5"
				>
					<Plus className="size-4" />
					{t('administration.spheraOptions.add')}
				</Button>
			</div>
			<ul className="divide-y rounded-md border px-3">
				{items.map((item) => (
					<OptionRow key={item.id} item={item} onDelete={onDelete} />
				))}
			</ul>
		</section>
	)
}

function LoadingMessage() {
	const { t } = useTranslation()
	return (
		<p className="p-6 text-center text-sm text-muted-foreground">
			{t('common.loading')}
		</p>
	)
}

export const SpheraOptionsPage = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { data, isLoading } = useSpheraOptions()
	const remove = useDeleteSpheraOption()
	const [deleting, setDeleting] = useState<SpheraOptionItem | null>(null)

	const confirmDelete = async () => {
		if (!deleting) return
		try {
			await remove.mutateAsync(deleting.id)
			toast.success(t('administration.spheraOptions.deleted'))
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
		setDeleting(null)
	}

	return (
		<AdminCardPage
			icon={<ListChecks className="size-5" />}
			title={t('administration.spheraOptions.title')}
			subtitle={t('administration.spheraOptions.subtitle')}
			onBack={() =>
				navigate(adminBackRoute(ROUTES.ADMINISTRATION.SPHERA_OPTIONS))
			}
			modal={
				<ConfirmDialog
					open={!!deleting}
					onOpenChange={(open) => !open && setDeleting(null)}
					title={t('administration.spheraOptions.deleteTitle')}
					onConfirm={confirmDelete}
					isPending={remove.isPending}
					confirmLabel={t('common.delete')}
					cancelLabel={t('common.cancel')}
					destructive
				>
					{t('administration.spheraOptions.deleteConfirm', {
						name: deleting?.name,
					})}
				</ConfirmDialog>
			}
		>
			<Tabs defaultValue="flags">
				<TabsList>
					<TabsTrigger value="flags">
						{t('administration.spheraOptions.tabs.flags')}
					</TabsTrigger>
					<TabsTrigger value="services">
						{t('administration.spheraOptions.tabs.services')}
					</TabsTrigger>
					<TabsTrigger value="branches">
						{t('administration.spheraOptions.tabs.branches')}
					</TabsTrigger>
					<TabsTrigger value="crs">
						{t('administration.spheraOptions.tabs.crs')}
					</TabsTrigger>
					<TabsTrigger value="params">
						{t('administration.spheraOptions.tabs.params')}
					</TabsTrigger>
				</TabsList>

				<TabsContent value="flags">
					{isLoading || !data ? (
						<LoadingMessage />
					) : (
						<OptionSection
							type="FLAG"
							title={t('administration.spheraOptions.flags')}
							items={data.flags}
							onDelete={setDeleting}
						/>
					)}
				</TabsContent>
				<TabsContent value="services">
					{isLoading || !data ? (
						<LoadingMessage />
					) : (
						<OptionSection
							type="SERVICE"
							title={t('administration.spheraOptions.services')}
							items={data.services}
							onDelete={setDeleting}
						/>
					)}
				</TabsContent>
				<TabsContent value="branches">
					<SpheraBranchList />
				</TabsContent>
				<TabsContent value="crs">
					<SpheraCrList />
				</TabsContent>
				<TabsContent value="params">
					<SpheraSettingsTab />
				</TabsContent>
			</Tabs>
		</AdminCardPage>
	)
}
